import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { StatCard } from "@/components/reusable-components/stat-card";
import { ApprovalInbox } from "@/components/supervisor/approvals/ApprovalInbox";
import { useFetchSubordinates } from "@/hooks/api/team/useFetchSubordinates";
import { useFetchApprovalRequests } from "@/hooks/api/approvals/useFetchApprovalRequests";
import { useFetchPendingOvertimeApprovals } from "@/hooks/api/overtime/useFetchPendingOvertimeApprovals";
import { useAuthStore } from "@/stores/useAuthStore";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";
import { useFeatureStore } from "@/stores/useFeatureStore";
import PageHeader from "@/components/reusable-components/header/page-header";

export const Route = createFileRoute("/supervisor/_layout/")({
  component: SupervisorDashboard,
});

function SupervisorDashboard() {
  const user = useAuthStore((s) => s.user);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const { isEnabled } = useFeatureStore();
  // Supervisor flows follow the role's module blocks (the gate 404s them).
  const leaveEnabled = isEnabled("leave");
  const overtimeSetting = useOrgSettingsStore(
    (s) => !!s.settings?.overtime_approval_required
  );
  const attendanceEnabled = isEnabled("attendance");

  const subordinatesQuery = useFetchSubordinates();
  const subordinates = subordinatesQuery.data?.results ?? [];
  const teamLoading = subordinatesQuery.isPending;
  const orgPendingQuery = useFetchApprovalRequests(
    {
      status: "PENDING",
      page_size: 1,
      // Counts work routed to you, so your own pending leave shouldn't inflate it.
      exclude_self: true,
    },
    { enabled: leaveEnabled },
  );
  const orgPending = orgPendingQuery.data?.count ?? 0;
  const orgLoading = orgPendingQuery.isPending;

  // Fetched whenever attendance is on, not only while approval is required:
  // chains started before the setting was switched off still wait on someone
  // (T-165), and hiding the card would strand them.
  const overtimePendingQuery = useFetchPendingOvertimeApprovals(
    { page_size: 1 },
    { enabled: attendanceEnabled }
  );
  const overtimePending = overtimePendingQuery.data?.count ?? 0;
  const overtimeLoading = overtimePendingQuery.isPending;
  const overtimeEnabled =
    attendanceEnabled && (overtimeSetting || overtimePending > 0);

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Supervisor Dashboard"
          description={
            (user?.first_name
              ? `Welcome back, ${user.first_name}. `
              : "Welcome back. ") +
            "Review what needs your decision and keep an eye on your team."
          }
        />

        {/* Stat row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {leaveEnabled && (
            <Link to="/supervisor/approvals" className="block no-underline">
              <StatCard
                label="Awaiting Leave Approvals"
                value={pendingCount ?? 0}
                loading={pendingCount === null}
                className="bg-linear-to-t from-primary/5 to-card shadow-xs"
              />
            </Link>
          )}
          {overtimeEnabled ? (
            <Link
              to="/supervisor/overtime-approvals"
              className="block no-underline"
            >
              <StatCard
                label="Awaiting Overtime Approvals"
                value={overtimePending}
                loading={overtimeLoading}
                className="bg-linear-to-t from-primary/5 to-card shadow-xs"
              />
            </Link>
          ) : (
            <Link to="/supervisor/team" className="block no-underline">
              <StatCard
                label="Team Members"
                value={subordinates.length}
                loading={teamLoading}
                className="bg-linear-to-t from-primary/5 to-card shadow-xs"
              />
            </Link>
          )}
          {leaveEnabled && (
            <Link
              to="/supervisor/requests"
              search={{ status: "PENDING" }}
              className="block no-underline"
            >
              <StatCard
                label="In Progress"
                value={orgPending}
                loading={orgLoading}
                className="bg-linear-to-t from-primary/5 to-card shadow-xs"
              />
            </Link>
          )}
        </div>

        {/* Pending inbox */}
        {leaveEnabled && (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Awaiting Leave Approvals</h2>
            <ApprovalInbox onCountChange={setPendingCount} />
          </section>
        )}
      </div>
    </SidebarInset>
  );
}
